import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAgencyCompany } from '@mint-lib/profile-helpers';

import { Alert } from '@myn/mui';

import type { UserModalViewType } from '../../organisms/UserModalNew/userModal.types.js';
import { UserModalType } from '../../organisms/UserModalNew/validation.js';

export type UserModalViewProps = {
  currentView: UserModalViewType;
  formInstance: UseFormReturn<UserModalType>;
  hasInitialValues: boolean;
};

const useAlert = ({
  currentView,
  formInstance,
  hasInitialValues,
}: UserModalViewProps) => {
  const { t } = useTranslation('@myn/permissions');

  const isAgencyCompany = useIsAgencyCompany();

  const alert = useMemo(() => {
    if (formInstance.formState.errors.isActive?.message === 'deactivated') {
      return (
        <Alert
          title={t(`This user can't be added to the workspace.`)}
          severity="error"
          subTitle={t(`Contact Mint ARM support if you need to add this user.`)}
        />
      );
    }

    if (
      !hasInitialValues &&
      currentView === 'nonExistingUserView' &&
      !isAgencyCompany
    ) {
      return (
        <Alert
          title={t(
            // eslint-disable-next-line @mint/no-special-characters
            `Remember to assign “Brand & Geographies” to New users or they won’t see any campaign.`,
          )}
          severity="info"
          subTitle={t(
            // eslint-disable-next-line @mint/no-special-characters
            `You can do so from the “Brand & Geographies” section of the “Company Profile” page.`,
          )}
        />
      );
    }

    return null;
  }, [
    currentView,
    formInstance.formState.errors,
    formInstance,
    hasInitialValues,
    isAgencyCompany,
    t,
  ]);

  return alert;
};

export default useAlert;
